// WMR.js - Updated with projectStatus and loading state for download
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TableContainer,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  Select,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import CustomTooltip from "../../components/CustomTooltip";
import { useAuth } from "../../context/authContext";
import axios from "axios";
import { url } from "../../constants/url";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks } from "date-fns";

// Updated status styles for projectStatus - Only On Track and Report Missing
const projectStatusStyles = {
  "ONTRACK": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  "REPORTMISSING": {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
  "On Track": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  "Report Missing": {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
};

// Updated headers for WMR - Using projectStatus
const headers = [
  { key: "Project Name", sortable: true, field: "projectName" },
  { key: "Start Date", sortable: true, field: "projectStartDate" },
  { key: "End Date", sortable: true, field: "projectEndDate" },
  { key: "Engineer", sortable: false, field: "assignedEngineerName" },
  { key: "WMR Week", sortable: true, field: "weekStartDate" },
  { key: "Status", sortable: false, field: "projectStatus" }, // Changed from Status to Status
  { key: "Actions", sortable: false },
];

const WMR = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Get auth context
  const { token, userData, loading: authLoading } = useAuth() || {};

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Check user role
  const isEngineerRole = userData?.role === "ENGINEER";
  const isAdminOrSuperAdmin = userData?.role === "ADMIN" || userData?.role === "SUPERADMIN";

  // State for data and loading
  const [wmrs, setWmrs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalWmrs, setTotalWmrs] = useState(0);
  const [pageSize] = useState(10);

  // State for UI controls
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);

  // State for download loading
  const [downloadingWmr, setDownloadingWmr] = useState(null);

  // Sort and filter states - Default sort: latest first (weekStartDate desc)
  const [sortConfig, setSortConfig] = useState(() => {
    const savedSort = localStorage.getItem("wmrSort");
    return savedSort ? JSON.parse(savedSort) : {
      field: "weekStartDate",
      direction: "desc"
    };
  });

  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem("wmrFilters");
    return savedFilters ? JSON.parse(savedFilters) : {
      search: "",
    };
  });

  // New filter states for multiple selection
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Week filter states
  const [weekFilter, setWeekFilter] = useState("ALL");
  const [customWeekRange, setCustomWeekRange] = useState({
    from: null,
    to: null,
  });
  const [showWeekPicker, setShowWeekPicker] = useState(false);

  // Filter menu states
  const [filterMenusOpen, setFilterMenusOpen] = useState({
    projects: false,
    statuses: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Save sort config to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("wmrSort", JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("wmrFilters", JSON.stringify(filters));
  }, [filters]);

  // Fetch projects for filter dropdown
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${url}/api/inhouse-projects/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: 1000,
          populate: "assignedEngineer"
        }
      });

      if (response.data.projects) {
        setProjects(response.data.projects);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
    }
  }, [token]);

  // Fetch WMRs with filters
  const fetchWMRs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page,
        limit: pageSize,
      };

      // Add search parameter
      if (filters.search && filters.search.trim() !== "") {
        params.search = filters.search.trim();
      }

      // Add project filter (multiple IDs as comma-separated string)
      if (selectedProjects.length > 0) {
        params.projectId = selectedProjects.join(",");
      }

      // Handle week filter options
      if (weekFilter !== "ALL") {
        let fromDate = null;
        let toDate = null;

        const today = new Date();

        switch (weekFilter) {
          case "THIS_WEEK":
            fromDate = startOfWeek(today, { weekStartsOn: 1 });
            toDate = endOfWeek(today, { weekStartsOn: 1 });
            break;
          case "LAST_WEEK":
            const lastWeek = subWeeks(today, 1);
            fromDate = startOfWeek(lastWeek, { weekStartsOn: 1 });
            toDate = endOfWeek(lastWeek, { weekStartsOn: 1 });
            break;
          case "NEXT_WEEK":
            const nextWeek = addWeeks(today, 1);
            fromDate = startOfWeek(nextWeek, { weekStartsOn: 1 });
            toDate = endOfWeek(nextWeek, { weekStartsOn: 1 });
            break;
          case "CUSTOM":
            if (customWeekRange.from && customWeekRange.to) {
              fromDate = new Date(customWeekRange.from);
              fromDate.setHours(0, 0, 0, 0);
              toDate = new Date(customWeekRange.to);
              toDate.setHours(23, 59, 59, 999);
            }
            break;
        }

        if (fromDate && toDate) {
          params.weekStartDate = fromDate.toISOString();
          params.weekEndDate = toDate.toISOString();
        }
      }

      // Add sort parameters based on sortConfig
      let sortParam = "weekDesc"; // Default: latest first
      if (sortConfig.field === "projectName") {
        sortParam = sortConfig.direction === "asc" ? "projectAz" : "projectZa";
      } else if (sortConfig.field === "weekStartDate") {
        sortParam = sortConfig.direction === "asc" ? "weekAsc" : "weekDesc";
      } else if (sortConfig.field === "projectStartDate") {
        sortParam = sortConfig.direction === "asc" ? "startDateAsc" : "startDateDesc";
      } else if (sortConfig.field === "projectEndDate") {
        sortParam = sortConfig.direction === "asc" ? "endDateAsc" : "endDateDesc";
      } else if (sortConfig.field === "createdAt") {
        sortParam = sortConfig.direction === "asc" ? "oldest" : "latest";
      }

      params.sort = sortParam;

      console.log("Fetching WMRs with params:", params);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: params,
      };

      // Use the WMR API endpoint
      const response = await axios.get(`${url}/api/wmr/fetch`, config);

      if (response.data.wmrs) {
        console.log("Raw WMR data from backend:", response.data.wmrs);

        const wmrData = response.data.wmrs.map(wmr => {
          console.log("Processing WMR:", wmr._id, "projectStatus:", wmr.projectStatus);

          // Extract project data
          let projectName = "N/A";
          let siteAddress = "-";
          let projectStartDate = null;
          let projectEndDate = null;
          let assignedEngineer = null;
          let assignedEngineerName = "Unassigned";
          let developerName = "-";

          if (wmr.projectId) {
            if (typeof wmr.projectId === 'object' && wmr.projectId !== null) {
              projectName = wmr.projectId.projectName || "N/A";
              siteAddress = wmr.projectId.siteAddress || "-";
              projectStartDate = wmr.projectId.projectStartDate || wmr.projectId.createdAt;
              projectEndDate = wmr.projectId.projectEndDate;
              developerName = wmr.projectId.developerName || "-";

              // Handle assignedEngineer
              if (wmr.projectId.assignedEngineer) {
                if (typeof wmr.projectId.assignedEngineer === 'object') {
                  assignedEngineer = wmr.projectId.assignedEngineer;
                  assignedEngineerName = wmr.projectId.assignedEngineer.name || "Unassigned";
                } else {
                  assignedEngineerName = wmr.projectId.assignedEngineerName || "Unassigned";
                }
              }
            }
          }

          // Handle createdBy
          let createdByName = "Unknown";
          if (wmr.createdBy) {
            if (typeof wmr.createdBy === 'object' && wmr.createdBy !== null) {
              createdByName = wmr.createdBy.name || wmr.createdBy.email || "Unknown";
            }
          }

          // Handle projectStatus - only allow ONTRACK, REPORTMISSING
          let wmrProjectStatus = wmr.projectStatus || "REPORTMISSING";

          // Normalize status values
          if (wmrProjectStatus === "ONTRACK") {
            wmrProjectStatus = "On Track";
          } else if (wmrProjectStatus === "REPORTMISSING") {
            wmrProjectStatus = "Report Missing";
          }

          return {
            ...wmr,
            // Enhanced project data
            projectName,
            siteAddress,
            projectStartDate,
            projectEndDate,
            developerName,
            assignedEngineer,
            assignedEngineerName,
            createdByName,
            projectStatus: wmrProjectStatus, // Use projectStatus instead of status
          };
        });

        // Apply project status filter on frontend
        const filteredWmrData = wmrData.filter(wmr => {
          if (selectedStatuses.length === 0) return true;
          return selectedStatuses.includes(wmr.projectStatus);
        });

        setWmrs(filteredWmrData);
        setTotalWmrs(response.data.total || wmrData.length);
        setTotalPages(Math.ceil((response.data.total || wmrData.length) / pageSize));
      } else {
        setWmrs([]);
        setTotalWmrs(0);
        setTotalPages(1);
      }

    } catch (err) {
      console.error("Error fetching WMR data:", err);
      setError("Failed to fetch WMR data");
      showSnackbar("Failed to fetch WMR data. Please try again.", "error");
      setWmrs([]);
      setTotalWmrs(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    page, pageSize, token, sortConfig, filters,
    selectedProjects, selectedStatuses,
    weekFilter, customWeekRange
  ]);

  // Initial fetch - only when auth is loaded and user data is available
  useEffect(() => {
    if (!authLoading && userData && token) {
      fetchWMRs();
      fetchProjects();
    }
  }, [fetchWMRs, fetchProjects, authLoading, userData, token]);

  // Check if any filter is applied
  const isFilterApplied = () => {
    return (
      filters.search !== "" ||
      selectedProjects.length > 0 ||
      selectedStatuses.length > 0 ||
      weekFilter !== "ALL" ||
      sortConfig.field !== "weekStartDate" ||
      sortConfig.direction !== "desc"
    );
  };

  // Handle sorting
  const handleSort = (field) => {
    const newSortConfig = {
      field,
      direction: sortConfig.field === field && sortConfig.direction === "asc" ? "desc" : "asc"
    };
    setSortConfig(newSortConfig);
    setPage(1);
  };

  // Get sort icon for a specific field
  const getSortIcon = (field) => {
    if (sortConfig.field === field) {
      return sortConfig.direction === "asc" ? "foldUp" : "foldDown";
    }
    return "sort"; // Default sort icon (not active)
  };

  // Handle global sort selection
  const handleGlobalSort = (sortType) => {
    let field, direction;

    switch (sortType) {
      case "LATEST":
        field = "weekStartDate";
        direction = "desc";
        break;
      case "OLDEST":
        field = "weekStartDate";
        direction = "asc";
        break;
      case "AZ":
        field = "projectName";
        direction = "asc";
        break;
      case "ZA":
        field = "projectName";
        direction = "desc";
        break;
      default:
        field = "weekStartDate";
        direction = "desc";
    }

    setSortConfig({ field, direction });
    setSortAnchorEl(null);
    setPage(1);
  };

  // Handle week filter selection
  const handleWeekFilterSelect = (filterValue) => {
    if (filterValue === "CUSTOM") {
      setShowWeekPicker(true);
      setCalendarAnchorEl(null);
    } else {
      setWeekFilter(filterValue);
      setCalendarAnchorEl(null);
      setPage(1);
    }
  };

  // Handle custom week range selection
  const handleCustomWeekRangeSelect = () => {
    if (customWeekRange.from && customWeekRange.to) {
      setWeekFilter("CUSTOM");
      setShowWeekPicker(false);
      setPage(1);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: "",
    });
    setSelectedProjects([]);
    setSelectedStatuses([]);
    setWeekFilter("ALL");
    setCustomWeekRange({ from: null, to: null });
    setSortConfig({
      field: "weekStartDate",
      direction: "desc"
    });
    setPage(1);
  };

  // Clear search only
  const clearSearch = () => {
    setFilters({ ...filters, search: "" });
    setPage(1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format week display
  const formatWeekDisplay = (weekStartDate, weekEndDate) => {
    if (!weekStartDate || !weekEndDate) return "-";
    const start = new Date(weekStartDate);
    const end = new Date(weekEndDate);

    const startDay = start.getDate();
    const startMonth = start.toLocaleDateString("en-GB", { month: "short" });
    const endDay = end.getDate();
    const endMonth = end.toLocaleDateString("en-GB", { month: "short" });
    const year = start.getFullYear().toString().slice(-2);

    if (startMonth === endMonth) {
      return `${startDay}-${endDay} ${startMonth} '${year}`;
    }
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} '${year}`;
  };

  // Get Status display text - Only On Track, Report Missing
  const getProjectStatusText = (status) => {
    if (!status) return "Report Missing";

    const statusStr = String(status).toUpperCase();

    if (statusStr === "ONTRACK" || statusStr === "ON TRACK") return "On Track";
    if (statusStr === "REPORTMISSING" || statusStr === "REPORT MISSING") return "Report Missing";

    // Default to Report Missing
    return "Report Missing";
  };

  // Handle download WMR with loading state
  const handleDownloadWMR = async (wmrId) => {
    setDownloadingWmr(wmrId);
    try {
      const response = await axios.get(`${url}/api/wmr/download/${wmrId}/report`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Get filename from response headers or generate one
      const filename = response.headers['content-disposition']
        ? response.headers['content-disposition'].split('filename=')[1].replace(/"/g, '')
        : `WMR_Report_${wmrId}.pdf`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      showSnackbar("WMR report downloaded successfully!", "success");
    } catch (error) {
      console.error("Error downloading WMR:", error);
      showSnackbar("Failed to download WMR report. Please try again.", "error");
    } finally {
      setDownloadingWmr(null);
    }
  };

  // Handle edit WMR - Apply same logic as EditReport.js
  const handleEditWMR = (wmr) => {
    if (!wmr._id) {
      showSnackbar("Missing WMR information", "error");
      return;
    }

    // Extract project ID and week dates from WMR data
    const projectId = wmr.projectId?._id || wmr.projectId;
    const weekStartDate = wmr.weekStartDate;
    const weekEndDate = wmr.weekEndDate;

    if (!projectId) {
      showSnackbar("Project not found for this WMR", "error");
      return;
    }

    // Navigate to edit screen with same structure as EditReport.js
    // INCLUDE BOTH PROJECT ID AND WMR ID IN URL to match App.js route
    navigate(`/progress/edit-wmr/${projectId}/${wmr._id}`, {
      state: {
        projectId: projectId,
        weekType: "current", // or calculate based on dates
        weekStartDate: weekStartDate,
        weekEndDate: weekEndDate,
        projectName: wmr.projectName,
        wmrId: wmr._id, // Pass wmrId in state as well
        fromWMRList: true, // Flag to indicate coming from WMR list
        existingWMR: wmr // Pass the entire wmr object for immediate data
      }
    });
  };

  // Handle add WMR
  const handleAddWMR = () => {
    navigate(`/progress/add-wmr`);
  };

  // Snackbar functions
  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Responsive table headers
  const getVisibleHeaders = () => {
    if (isMobile) {
      return headers.filter(header =>
        ["Project Name", "WMR Week", "Status", "Actions"].includes(header.key)
      );
    }
    if (isTablet) {
      return headers.filter(header =>
        !["Start Date", "End Date"].includes(header.key)
      );
    }
    return headers;
  };

  // Get project display name safely
  const getProjectName = (item) => {
    return item.projectName || "N/A";
  };

  // Get engineer name safely
  const getEngineerName = (item) => {
    if (item.projectId?.assignedEngineerName) {
      return item.projectId.assignedEngineerName;
    }

    if (item.assignedEngineerName) {
      return item.assignedEngineerName;
    }

    if (typeof item.assignedEngineer === 'object' && item.assignedEngineer !== null) {
      return item.assignedEngineer.name || "Unassigned";
    }

    if (item.projectId?.assignedEngineer?.name) {
      return item.projectId.assignedEngineer.name;
    }

    return "Unassigned";
  };

  // Get week display
  const getWeekDisplay = (item) => {
    if (item.weekStartDate && item.weekEndDate) {
      return formatWeekDisplay(item.weekStartDate, item.weekEndDate);
    }
    return "-";
  };

  // Responsive column rendering
  const renderTableCell = (header, item) => {
    const getFieldValue = (field) => {
      switch (field) {
        case "projectName":
          return getProjectName(item);
        case "projectStartDate":
          return formatDate(item.projectStartDate);
        case "projectEndDate":
          return formatDate(item.projectEndDate);
        case "assignedEngineerName":
          return getEngineerName(item);
        case "weekStartDate":
          return getWeekDisplay(item);
        case "projectStatus":
          return getProjectStatusText(item.projectStatus);
        default:
          return item[field] || "-";
      }
    };

    switch (header.key) {
      case "Project Name":
        return (
          <TableCell key={header.key}>
            <Box>
              <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                {getFieldValue("projectName")}
              </Typography>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.MUTED_TEXT,
                }}
              >
                Developer: {item.developerName || "-"}
              </Typography>
            </Box>
          </TableCell>
        );

      case "Start Date":
      case "End Date":
      case "Engineer":
        return (
          <TableCell key={header.key} sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE }}>
            {getFieldValue(header.field)}
          </TableCell>
        );

      case "WMR Week":
        const weekDisplay = getFieldValue("weekStartDate");
        return (
          <TableCell key={header.key}>
            <Typography sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE }}>
              {weekDisplay}
            </Typography>
            {/* <Typography sx={{ 
              ...TYPOGRAPHY.BODY_SMALL, 
              color: COLORS.MUTED_TEXT,
              mt: 0.5
            }}>
              Created by: {item.createdByName || "Unknown"}
            </Typography> */}
          </TableCell>
        );

      case "Status": // Updated from "Status"
        const statusText = getProjectStatusText(item.projectStatus);

        return (
          <TableCell key={header.key}>
            <Chip
              label={statusText}
              sx={{
                ...(projectStatusStyles[statusText] || projectStatusStyles["REPORTMISSING"]),
                ...TYPOGRAPHY.BODY_SMALL,
                height: 'auto',
                minHeight: 24,
                fontWeight: 500,
                borderRadius: 4,
                '& .MuiChip-label': {
                  px: 1.2,
                  py: 0.3,
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  lineHeight: 1.3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                maxWidth: '110px',
                wordBreak: 'break-word',
              }}
            />
          </TableCell>
        );

      case "Actions":
        return (
          <TableCell key={header.key}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

              {/* Download button with loading state */}
              <CustomTooltip title="Download WMR Report">
                <IconButton
                  size="small"
                  onClick={() => handleDownloadWMR(item._id)}
                  disabled={downloadingWmr === item._id}
                >
                  {downloadingWmr === item._id ? (
                    <CircularProgress size={20} color="primary" />
                  ) : (
                    <Icon name="download1" color={COLORS.PRIMARY} size={20} />
                  )}
                </IconButton>
              </CustomTooltip>

              {/* Edit button with navigation to edit-wmr screen */}
              {(isAdminOrSuperAdmin) && (
                <CustomTooltip title="Edit WMR">
                  <IconButton
                    size="small"
                    onClick={() => handleEditWMR(item)}
                  >
                    <Icon name="Action3" color={COLORS.PRIMARY} size={20} />
                  </IconButton>
                </CustomTooltip>
              )}

            </Box>
          </TableCell>
        );

      default:
        return null;
    }
  };

  // Calculate displayed range for pagination
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalWmrs);

  // Get week filter display text
  const getWeekFilterDisplay = () => {
    if (weekFilter === "ALL") return "All Weeks";
    if (weekFilter === "THIS_WEEK") return "This Week";
    if (weekFilter === "LAST_WEEK") return "Last Week";
    if (weekFilter === "NEXT_WEEK") return "Next Week";
    if (weekFilter === "CUSTOM" && customWeekRange.from && customWeekRange.to) {
      const from = formatDate(customWeekRange.from);
      const to = formatDate(customWeekRange.to);
      return `${from} - ${to}`;
    }
    return "Select Week";
  };

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show login prompt if not authenticated
  if (!authLoading && !userData) {
    return (
      <Box sx={{
        p: 3,
        textAlign: 'center'
      }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          Please log in to view WMRs
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            mb: 3,
            gap: { xs: 2, sm: 2 },
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
            Weekly Monitoring Reports (WMR)
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: { sm: "auto" },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {/* Search Bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: { xs: 1, sm: 'none' },
                minWidth: { xs: '100%', sm: 300 },
                px: 2,
                py: 0.7,
                borderRadius: 1.5,
                border: `1px solid ${COLORS.PRIMARY}`,
                backgroundColor: COLORS.BACKGROUND,
                color: COLORS.PRIMARY,
              }}
            >
              <Icon name="search" sx={{ fontSize: 18, color: "currentColor" }} />
              <InputBase
                placeholder="Search projects or WMRs..."
                value={filters.search}
                onChange={(e) => {
                  setFilters({ ...filters, search: e.target.value });
                  setPage(1);
                }}
                sx={{
                  flex: 1,
                  fontFamily: 'inherit',
                  ...TYPOGRAPHY.BODY_STANDARD,
                  color: COLORS.TEXT,
                  ml: 1,
                }}
              />
              {filters.search && (
                <IconButton
                  size="small"
                  onClick={clearSearch}
                  sx={{ padding: 0.5 }}
                >
                  <Icon name="close2" sx={{ fontSize: 14 }} color={COLORS.ERROR} />
                </IconButton>
              )}
            </Box>

            {/* Week Menu - Show active filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Week Filter">
                <IconButton
                  onClick={(e) => setCalendarAnchorEl(e.currentTarget)}
                  sx={{
                    color: weekFilter !== "ALL" ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="calendar2" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {/* Sort Menu - Show active filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Sort Options">
                <IconButton
                  onClick={(e) => setSortAnchorEl(e.currentTarget)}
                  sx={{
                    color: sortConfig.direction !== "desc" ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="sort" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {/* Filter Button - Toggles filter section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Show Filters">
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    color: showFilters ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="list" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {/* Refresh / Clear */}
            <CustomTooltip title={isFilterApplied() ? "Clear Filters" : "Refresh"}>
              <IconButton
                onClick={isFilterApplied() ? clearFilters : fetchWMRs}
              >
                <Icon
                  name="rotate"
                  color={isFilterApplied() ? COLORS.ERROR : COLORS.SECONDARY}
                />
              </IconButton>
            </CustomTooltip>
          </Box>
        </Box>

        {/* Mobile Add WMR Button */}
        {(isAdminOrSuperAdmin || isEngineerRole) && (
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', mb: 2 }}>
            <Button
              startIcon={<Icon name="plus2" />}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                borderRadius: 2,
                ...TYPOGRAPHY.H4,
                px: 2.5,
                "&:hover": { bgcolor: COLORS.SECONDARY },
                width: '100%',
              }}
              onClick={handleAddWMR}
            >
              Add New WMR
            </Button>
          </Box>
        )}

        {/* Filter Section */}
        {showFilters && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}>
            {/* Project Filter */}
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  mb: 0.5,
                  fontWeight: 600
                }}
              >
                Project
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedProjects}
                  onChange={(e) => {
                    setSelectedProjects(e.target.value);
                    setPage(1);
                  }}
                  open={filterMenusOpen.projects}
                  onOpen={() => setFilterMenusOpen(prev => ({ ...prev, projects: true }))}
                  onClose={() => setFilterMenusOpen(prev => ({ ...prev, projects: false }))}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontSize: '0.875rem'
                        }}>
                          Select Projects
                        </Typography>
                      );
                    }
                    return selected.length <= 2
                      ? selected.map(id => {
                        const project = projects.find(p => p._id === id);
                        return project?.projectName || id;
                      }).join(', ')
                      : `${selected.length} selected`;
                  }}
                  displayEmpty
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 250,
                        width: 280,
                        overflow: 'auto',
                        mt: 0.5,
                        '& .MuiMenuItem-root': {
                          minHeight: 40,
                          padding: '6px 12px',
                        },
                        '& .MuiCheckbox-root': {
                          padding: '6px',
                        }
                      }
                    },
                  }}
                >
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <MenuItem
                        key={project._id}
                        value={project._id}
                      >
                        <Checkbox
                          checked={selectedProjects.indexOf(project._id) > -1}
                          size="small"
                        />
                        <ListItemText
                          primary={project.projectName}
                          sx={{
                            margin: 0,
                            '& .MuiTypography-root': {
                              fontSize: '0.85rem',
                            }
                          }}
                        />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <Typography sx={{
                        fontSize: '0.85rem',
                        color: COLORS.TEXT,
                        fontStyle: 'italic'
                      }}>
                        No projects available
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* Status Filter - Only On Track, Report Missing */}
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  mb: 0.5,
                  fontWeight: 600
                }}
              >
                Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedStatuses}
                  onChange={(e) => {
                    setSelectedStatuses(e.target.value);
                    setPage(1);
                  }}
                  open={filterMenusOpen.statuses}
                  onOpen={() => setFilterMenusOpen(prev => ({ ...prev, statuses: true }))}
                  onClose={() => setFilterMenusOpen(prev => ({ ...prev, statuses: false }))}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontSize: '0.875rem'
                        }}>
                          Select Statuses
                        </Typography>
                      );
                    }
                    return selected.length <= 2
                      ? selected.map(s => getProjectStatusText(s)).join(', ')
                      : `${selected.length} selected`;
                  }}
                  displayEmpty
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 250,
                        width: 280,
                        overflow: 'auto',
                        mt: 0.5,
                        '& .MuiMenuItem-root': {
                          minHeight: 40,
                          padding: '6px 12px',
                        },
                        '& .MuiCheckbox-root': {
                          padding: '6px',
                        }
                      }
                    },
                  }}
                >
                  {/* Only allow these project statuses */}
                  {[
                    "On Track", "Report Missing"
                  ].map((status) => (
                    <MenuItem
                      key={status}
                      value={status}
                    >
                      <Checkbox
                        checked={selectedStatuses.indexOf(status) > -1}
                        size="small"
                      />
                      <ListItemText
                        primary={getProjectStatusText(status)}
                        sx={{
                          margin: 0,
                          '& .MuiTypography-root': {
                            fontSize: '0.85rem',
                          }
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Error Message */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table - Responsive Container */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: "transparent",
            overflow: "auto",
            width: "100%"
          }}
        >
          <TableContainer>
            <Table
              sx={{
                backgroundColor: "transparent",
                minWidth: isMobile ? 500 : "auto",
                "& thead th": {
                  backgroundColor: "transparent",
                },
                "& tbody td": {
                  backgroundColor: "transparent",
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {getVisibleHeaders().map((header) => (
                    <TableCell
                      key={header.key}
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        whiteSpace: "nowrap",
                        py: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <span>{header.key}</span>
                        {header.sortable && (
                          <CustomTooltip title={
                            sortConfig.field === header.field
                              ? `Sorted ${sortConfig.direction === 'asc' ? 'Ascending' : 'Descending'}`
                              : `Sort by ${header.key}`
                          }>
                            <IconButton
                              onClick={() => handleSort(header.field)}
                              sx={{
                                padding: 0,
                                width: 24,
                                height: 24,
                              }}
                            >
                              <Icon
                                name={getSortIcon(header.field)}
                                color={
                                  sortConfig.field === header.field
                                    ? COLORS.ERROR
                                    : COLORS.LIGHT_SECONDARY
                                }
                              />
                            </IconButton>
                          </CustomTooltip>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={getVisibleHeaders().length} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : wmrs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={getVisibleHeaders().length} align="center" sx={{ py: 3 }}>
                      <Typography sx={{ color: COLORS.TEXT }}>
                        {isFilterApplied()
                          ? "No WMRs found matching your filters"
                          : "No WMRs found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  wmrs.slice((page - 1) * pageSize, page * pageSize).map((wmr) => (
                    <TableRow
                      key={wmr._id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        }
                      }}
                    >
                      {getVisibleHeaders().map((header) => renderTableCell(header, wmr))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {!loading && wmrs.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                p: 2,
              }}
            >
              <Typography sx={{ fontSize: 13, color: "#777" }}>
                {startIndex} - {endIndex} of {totalWmrs}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CustomTooltip title="Previous">
                  <span>
                    <IconButton
                      onClick={() => setPage(prev => Math.max(1, prev - 1))}
                      disabled={page === 1}
                      sx={{
                        width: 32,
                        height: 32,
                        color: page === 1 ? COLORS.LIGHT_SECONDARY : COLORS.PRIMARY,
                      }}
                    >
                      <Icon name="chevron-left" />
                    </IconButton>
                  </span>
                </CustomTooltip>

                {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 3) {
                    pageNum = i + 1;
                  } else if (page === 1) {
                    pageNum = i + 1;
                  } else if (page === totalPages) {
                    pageNum = totalPages - 2 + i;
                  } else {
                    pageNum = page - 1 + i;
                  }

                  if (pageNum > 0 && pageNum <= totalPages) {
                    return (
                      <Button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        sx={{
                          minWidth: 32,
                          height: 32,
                          fontSize: 12,
                          fontWeight: 500,
                          bgcolor: page === pageNum ? COLORS.PRIMARY : "transparent",
                          color: page === pageNum ? "#fff" : COLORS.TEXT,
                          border: page === pageNum
                            ? "none"
                            : `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: page === pageNum
                              ? COLORS.PRIMARY
                              : COLORS.LIGHT_BACKGROUND,
                          },
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  }
                  return null;
                })}

                <CustomTooltip title="Next">
                  <span>
                    <IconButton
                      onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={page === totalPages}
                      sx={{
                        width: 32,
                        height: 32,
                        color: page === totalPages ? COLORS.LIGHT_SECONDARY : COLORS.PRIMARY,
                      }}
                    >
                      <Icon name="chevron-right" />
                    </IconButton>
                  </span>
                </CustomTooltip>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Global Sort Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={() => setSortAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
              minWidth: 160,
            },
          }}
        >
          {[
            { label: "Latest First", value: "LATEST", icon: "foldUp" },
            { label: "Oldest First", value: "OLDEST", icon: "foldDown" },
            { label: "Project Name A-Z", value: "AZ", icon: "sort" },
            { label: "Project Name Z-A", value: "ZA", icon: "sort" },
          ].map((item) => (
            <MenuItem
              key={item.value}
              onClick={() => handleGlobalSort(item.value)}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
              }}
            >
              <Icon name={item.icon} color={COLORS.TEXT} />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          ))}
        </Menu>

        {/* Week Menu */}
        <Menu
          anchorEl={calendarAnchorEl}
          open={Boolean(calendarAnchorEl)}
          onClose={() => setCalendarAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
            },
          }}
        >
          {[
            { label: "All Weeks", value: "ALL", icon: "calendarAll" },
            { label: "This Week", value: "THIS_WEEK", icon: "calendarToday" },
            { label: "Last Week", value: "LAST_WEEK", icon: "calendarYesterday" },
            { label: "Next Week", value: "NEXT_WEEK", icon: "calendarTomorrow" },
            { label: "Custom Week Range", value: "CUSTOM", icon: "calendarCustom" },
          ].map((item) => {
            const isActive = weekFilter === item.value;

            return (
              <MenuItem
                key={item.value}
                onClick={() => handleWeekFilterSelect(item.value)}
                sx={{
                  gap: 1.5,
                  borderRadius: 1,
                  color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
                }}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
                />
                <Typography sx={TYPOGRAPHY.BODY_LARGE}>
                  {item.label}
                </Typography>
              </MenuItem>
            );
          })}
        </Menu>

        {/* Custom Week Range Picker Dialog */}
        <Dialog
          open={showWeekPicker}
          onClose={() => setShowWeekPicker(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="calendarCustom" color={COLORS.PRIMARY} />
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
                Select Week Range
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <DatePicker
                label="From Date"
                value={customWeekRange.from}
                onChange={(newValue) => {
                  setCustomWeekRange(prev => ({ ...prev, from: newValue }));
                }}
                slotProps={{ textField: { fullWidth: true } }}
                maxDate={customWeekRange.to || new Date()}
              />
              <DatePicker
                label="To Date"
                value={customWeekRange.to}
                onChange={(newValue) => {
                  setCustomWeekRange(prev => ({ ...prev, to: newValue }));
                }}
                slotProps={{ textField: { fullWidth: true } }}
                minDate={customWeekRange.from}
                maxDate={new Date()}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setShowWeekPicker(false)}
              sx={{
                color: COLORS.TEXT,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCustomWeekRangeSelect}
              disabled={!customWeekRange.from || !customWeekRange.to}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                "&:hover": { bgcolor: COLORS.SECONDARY },
                "&:disabled": { bgcolor: COLORS.LIGHT_SECONDARY }
              }}
            >
              Apply Week Range
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default WMR;